/**
 * Pure helpers for the Settings page: source-priority reordering and profile
 * form (de)serialization. Kept side-effect-free so they can be unit-tested
 * without a DOM.
 */

/** The default order the server falls back to when nothing is stored. */
export const DEFAULT_SOURCE_PRIORITY = ['apple_health', 'fitbit', 'manual'];

/** Human labels for the known metric sources. */
export const SOURCE_LABELS: Record<string, string> = {
  apple_health: 'Apple Health',
  fitbit: 'Fitbit',
  manual: 'Manual entry',
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/**
 * Move the item at `index` one slot toward `dir` ('up' | 'down'). Returns a new
 * array; a no-op (out of bounds) returns the input unchanged.
 */
export function reorder(list: string[], index: number, dir: 'up' | 'down'): string[] {
  const target = dir === 'up' ? index - 1 : index + 1;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved!);
  return next;
}

/**
 * Ensure every known source appears exactly once: keep the stored order, drop
 * unknown entries, and append any known source that's missing (so a newly-added
 * source like Fitbit surfaces even if the stored list predates it).
 */
export function normalizePriority(stored: unknown): string[] {
  const arr = Array.isArray(stored) ? stored.filter((s): s is string => typeof s === 'string') : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    if (DEFAULT_SOURCE_PRIORITY.includes(s) && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  for (const s of DEFAULT_SOURCE_PRIORITY) {
    if (!seen.has(s)) out.push(s);
  }
  return out;
}

export interface ProfileForm {
  age: string;
  heightCm: string;
  goals: string;
}

/** Parse a stored profile object into editable string fields. */
export function profileToForm(stored: unknown): ProfileForm {
  const p = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const goals = Array.isArray(p.goals) ? p.goals.filter((g): g is string => typeof g === 'string') : [];
  return {
    age: typeof p.age === 'number' ? String(p.age) : '',
    heightCm: typeof p.heightCm === 'number' ? String(p.heightCm) : '',
    goals: goals.join(', '),
  };
}

/**
 * Serialize the profile form back to the stored shape. Blank numeric fields are
 * omitted; goals are split on commas and trimmed. Returns null when a numeric
 * field is present but not a finite number (caller shows a validation error).
 */
export function formToProfile(form: ProfileForm): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  for (const key of ['age', 'heightCm'] as const) {
    const raw = form[key].trim();
    if (raw === '') continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    out[key] = n;
  }
  const goals = form.goals
    .split(',')
    .map((g) => g.trim())
    .filter((g) => g.length > 0);
  if (goals.length > 0) out.goals = goals;
  return out;
}

// --- Home location (weather / pollen) --------------------------------------

export interface HomeLocationForm {
  lat: string;
  lon: string;
}

/** Parse the stored `home_location` object into editable string fields. */
export function homeLocationToForm(stored: unknown): HomeLocationForm {
  const p = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  return {
    lat: typeof p.lat === 'number' ? String(p.lat) : '',
    lon: typeof p.lon === 'number' ? String(p.lon) : '',
  };
}

/**
 * Validate + serialize a lat/lon pair. Returns null when either field is blank,
 * non-numeric, or out of geographic range (lat ±90, lon ±180) — the caller then
 * shows an inline error rather than PUTting a bad location.
 */
export function formToHomeLocation(form: HomeLocationForm): { lat: number; lon: number } | null {
  const latRaw = form.lat.trim();
  const lonRaw = form.lon.trim();
  if (latRaw === '' || lonRaw === '') return null;
  const lat = Number(latRaw);
  const lon = Number(lonRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}
